'use client';

import { useCallback, useRef } from 'react';
import { snapTimeToFrame } from '@/lib/editor/frameGrid';

interface Props {
  pixelsPerSecond: number;
  onTrimIn: (deltaSeconds: number) => void;
  onTrimOut: (deltaSeconds: number) => void;
  onCommit?: () => void;
}

export function TimelineTrimHandles({ pixelsPerSecond, onTrimIn, onTrimOut, onCommit }: Props) {
  const dragState = useRef<{ which: 'in' | 'out'; startX: number; pendingSeconds: number } | null>(null);

  const onPointerDown = useCallback(
    (which: 'in' | 'out') => (e: React.PointerEvent) => {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragState.current = { which, startX: e.clientX, pendingSeconds: 0 };
      e.stopPropagation();
    },
    []
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragState.current) return;
      const deltaPx = e.clientX - dragState.current.startX;
      dragState.current.pendingSeconds += deltaPx / Math.max(pixelsPerSecond, 1);
      const deltaSec = snapTimeToFrame(Math.abs(dragState.current.pendingSeconds)) * Math.sign(dragState.current.pendingSeconds);
      dragState.current.startX = e.clientX;
      if (deltaSec === 0) return;
      dragState.current.pendingSeconds -= deltaSec;
      if (dragState.current.which === 'in') onTrimIn(deltaSec);
      else onTrimOut(deltaSec);
      e.stopPropagation();
    },
    [pixelsPerSecond, onTrimIn, onTrimOut]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).hasPointerCapture(e.pointerId)) {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      }
      dragState.current = null;
      onCommit?.();
      e.stopPropagation();
    },
    [onCommit]
  );

  return (
    <>
      <div
        data-timeline-control
        onPointerDown={onPointerDown('in')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={(e) => e.stopPropagation()}
        className="group absolute left-0 top-0 z-40 flex h-full w-2.5 cursor-ew-resize items-center justify-center"
      >
        <span className="h-8 w-0.5 rounded-full bg-white/60 transition group-hover:bg-white group-active:bg-white group-active:shadow-[0_0_0_1px_rgba(91,53,255,0.85)]" />
      </div>
      <div
        data-timeline-control
        onPointerDown={onPointerDown('out')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={(e) => e.stopPropagation()}
        className="group absolute right-0 top-0 z-40 flex h-full w-2.5 cursor-ew-resize items-center justify-center"
      >
        <span className="h-8 w-0.5 rounded-full bg-white/60 transition group-hover:bg-white group-active:bg-white group-active:shadow-[0_0_0_1px_rgba(91,53,255,0.85)]" />
      </div>
    </>
  );
}
